import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { dismissNotification, getNotifications, getNotificationsHistory, markNotificationAsRead } from '../../services/userService';
import AuthContext from '../Auth/Middleware/AuthContext';
import { socket } from '../../utils/socket';
import InfiniteScroll from "react-infinite-scroll-component";

export default function Notifications() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [id, setId] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [active, setActive] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const authContext = useContext(AuthContext);
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    if (authContext.loginUserId) {
      if (!socket?.connected) {
        socket.connect();
        socket.on("connect", () => {
          socket.emit("mapUser", { sid: socket.id, user_id: parseInt(authContext.loginUserId) });
        });
      }
    }
    socket.on("notification", (data) => {
      window.toastr.info(data);
      if (active) {
        fetchAllNotifications(true);
      } else {
        fetchNotificationsHistory(true);
      }
    });

    return () => {
      socket.off("notification");
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const fetchAllNotifications = async (reset = false) => {
    try {
      if (reset) {
        setPage(0);
        setHasMore(true);
      }
      const offset = reset ? 0 : page * limit;

      const response = await getNotifications({ offset, limit });
      if (response?.success) {
        const newNotifications = reset ? response.data.notifications : [...notifications, ...response.data.notifications];
        setNotifications(newNotifications);
        setPage(prev => prev + 1);
        setNotificationCount(response.data?.count);
        setHasMore(newNotifications.length < response.data?.count);
        setActive(true);
      }
    } catch (err) {
      console.log('err', err);
    }
  };

  useEffect(() => {
    fetchAllNotifications(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkAsRead = async (notificationId, isRead) => {
    try {
      const response = await markNotificationAsRead(notificationId, isRead);
      if (response?.success) {
        if (active) {
          fetchAllNotifications(true);
        } else {
          fetchNotificationsHistory(true);
        }
      }
    } catch (err) {
      console.log('Something went wrong!');
    }
  };

  const fetchNotificationsHistory = async (reset = false) => {
    try {
      if (reset) {
        setPage(0);
        setHasMore(true);
      }
      const offset = reset ? 0 : page * limit;
      const response = await getNotificationsHistory({ offset, limit });
      if (response?.success) {
        const newNotifications = reset ? response.data.notifications : [...notifications, ...response.data.notifications];
        setNotifications(newNotifications);
        setPage(prev => prev + 1);
        setHasMore(newNotifications.length < response.data.count);
        setNotificationCount(response.data?.count);
        setActive(false);
      } else {
        window.toastr.error(response?.message || 'Could not fetch notifications!');
      }
    } catch (err) {
      window.toastr.error(err?.message || 'Something went wrong!');
    }
  };

  const handleDismiss = async (notificationId) => {
    try {
      setId(notificationId);
      const response = await dismissNotification(notificationId);
      if (response?.success) {
        fetchAllNotifications(true);
      }
    } catch (err) {
      window.toastr.error(err?.message || 'Something went wrong!');
    }
    setId('');
  };

  return (
    <div className="d-flex align-items-center ms-2 ms-lg-3">
      <div
        className={`btn btn-icon btn-active-primary position-relative w-35px h-35px w-md-40px h-md-40px ${isDropdownOpen ? 'show' : ''}`}
        data-kt-menu-trigger="{default:'click', lg: 'hover'}"
        data-kt-menu-attach="parent"
        data-kt-menu-placement="bottom-end"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Toggle notifications dropdown"
      >
        <span className="svg-icon svg-icon-1">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path opacity="0.3" d="M12 22C13.6569 22 15 20.6569 15 19C15 17.3431 13.6569 16 12 16C10.3431 16 9 17.3431 9 19C9 20.6569 10.3431 22 12 22Z" fill="currentColor" />
            <path d="M19 15V18C19 18.6 18.6 19 18 19H6C5.4 19 5 18.6 5 18V15C6.1 15 7 14.1 7 13V10C7 7.6 8.7 5.6 11 5.1V3C11 2.4 11.4 2 12 2C12.6 2 13 2.4 13 3V5.1C15.3 5.6 17 7.6 17 10V13C17 14.1 17.9 15 19 15ZM11 10C11 9.4 11.4 9 12 9C12.6 9 13 8.6 13 8C13 7.4 12.6 7 12 7C10.3 7 9 8.3 9 10C9 10.6 9.4 11 10 11C10.6 11 11 10.6 11 10Z" fill="currentColor" />
          </svg>
          {notificationCount > 0 && (
            <span
              className="badge badge-danger notification position-absolute justify-content-center"
              style={{
                transform: 'translate(-10px, 0)',
                height: '20px',
                width: notificationCount > 99 ? 'fit-content' : '20px',
                borderRadius: '10px',
              }}
            >
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
        </span>
      </div>
      <div className={`menu menu-sub menu-sub-dropdown menu-column w-350px ${isDropdownOpen ? 'show' : ''}`} data-kt-menu="true">
        <div className="card">
          <div className="card-header align-items-center">
            <h3 className="card-title">Notifications</h3>
            <button
              className="btn btn-light-danger btn-sm w-fit h-fit d-flex justify-content-center"
              type="button"
              onClick={() => active ? fetchNotificationsHistory(true): fetchAllNotifications(true)}
            >
              {active ? 'View All' : 'Back'}
            </button>
          </div>
          <div className="card-body p-0">
            <InfiniteScroll
              dataLength={notifications?.length ?? 0}
              next={active ? fetchAllNotifications : fetchNotificationsHistory}
              hasMore={hasMore}
              height={400}
              loader={<span className="spinner-border p-2 spinner-border-sm m-auto text-center d-block" role="status" />}
            >
              {notifications?.length > 0 && notifications?.map((notification, index) => (
                <div
                  key={notification.id}
                  
                  className={`w-100d-flex align-items-center justify-content-between mb-4 ${notification?.isRead ? 'text-gray' : ''}`}
                  onClick={() => handleMarkAsRead(notification?.id, true)}
                >
                  <div className="d-flex align-items-center py-3 px-5">
                    <div className="symbol symbol-40px symbol-circle me-5">
                      <span className="symbol-label bg-light-primary">
                        <span className={`svg-icon svg-icon-1 ${notification?.isRead ? 'svg-icon-light-secondary' : 'svg-icon-primary'}`}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path opacity="0.3" d="M21.25 18.525L13.05 21.825C12.35 22.125 11.65 22.125 10.95 21.825L2.75 18.525C1.75 18.125 1.75 16.725 2.75 16.325L4.04999 15.825L10.25 18.325C10.85 18.525 11.45 18.625 12.05 18.625C12.65 18.625 13.25 18.525 13.85 18.325L20.05 15.825L21.35 16.325C22.35 16.725 22.35 18.125 21.25 18.525ZM13.05 16.425L21.25 13.125C22.25 12.725 22.25 11.325 21.25 10.925L13.05 7.62502C12.35 7.32502 11.65 7.32502 10.95 7.62502L2.75 10.925C1.75 11.325 1.75 12.725 2.75 13.125L10.95 16.425C11.65 16.725 12.45 16.725 13.05 16.425Z" fill="currentColor" />
                            <path d="M11.05 11.025L2.84998 7.725C1.84998 7.325 1.84998 5.925 2.84998 5.525L11.05 2.225C11.75 1.925 12.45 1.925 13.15 2.225L21.35 5.525C22.35 5.925 22.35 7.325 21.35 7.725L13.05 11.025C12.45 11.325 11.65 11.325 11.05 11.025Z" fill="currentColor" />
                          </svg>
                        </span>
                      </span>
                    </div>
                    <div className="mb-1 pe-3 flex-grow-1">
                      {notification?.message?.startsWith("Your requested") ? <Link to={`/manager-reports`} state={{ reports: "view"}}>
                        {/* eslint-disable-next-line */}
                        <div className={`${notification?.isRead ? 'text-gray-600' : 'text-dark'} fs-6 fw-semibold text-hover-primary`} dangerouslySetInnerHTML={{ __html: notification?.message }} title={notification?.message} />
                      </Link> :
                        <Link to={`/process-chart/${notification?.ChartId}`}>
                        {/* eslint-disable-next-line */}
                        <div className={`${notification?.isRead ? 'text-gray-600' : 'text-dark'} fs-6 fw-semibold text-hover-primary`} dangerouslySetInnerHTML={{ __html: notification?.message }} title={notification?.message} />
                      </Link>}
                      <div style={{ cursor: 'pointer' }} className={`${notification?.isRead ? 'text-gray-600' : 'text-dark'} fw-semibold fs-7"`} title={moment.utc(notification.timestamp, 'YYYY-MM-DD hh:mm A').local().format('DD-MM-YYYY HH:mm A')}>
                        {(moment.utc(notification.timestamp, 'YYYY-MM-DD hh:mm A').local().fromNow())}
                      </div>
                    </div>
                    <button
                      className="border-0 bg-transparent"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification.id, !notification?.isRead);
                      }}
                      disabled={id}
                      aria-label="button"
                      title={notification?.isRead ? 'Mark as unread' : 'Mark as read'}
                    >
                      {notification?.isRead ?
                        <svg role="graphics-symbol" viewBox="0 0 20 20" className="notificationUnread" style={{ width: '21px', height: '21px', display: 'block', fill: 'var(--bs-text-muted)', flexShrink: '0', padding: '0px', color: 'var(--bs-text-muted)' }}><path d="M3.622 12.683v-5.25c0-.62.05-1.172.15-1.655.105-.483.333-.9.684-1.25.351-.352.768-.58 1.251-.684a7.906 7.906 0 0 1 1.654-.157h4.69a2.893 2.893 0 0 0-.185 1.086 1.325 1.325 0 0 0-.007.13l.014.13H7.204c-.36 0-.697.032-1.012.096-.314.064-.567.189-.758.376a1.514 1.514 0 0 0-.376.759c-.06.31-.09.645-.09 1.005v5.605c0 .355.03.688.09.998.063.305.189.554.376.745.191.187.442.315.752.383.314.064.649.096 1.004.096h5.62c.35 0 .68-.032.99-.096.315-.064.566-.191.753-.383.191-.191.319-.44.382-.745.064-.31.096-.643.096-.998V8.191l.123.014c.046 0 .091-.002.137-.007.191.01.378-.002.56-.034.183-.036.356-.087.52-.15v4.669c0 .629-.052 1.184-.157 1.668-.1.483-.324.9-.67 1.25-.356.351-.777.58-1.265.684a7.944 7.944 0 0 1-1.66.157H7.374c-.624 0-1.18-.052-1.668-.157a2.478 2.478 0 0 1-1.25-.683 2.411 2.411 0 0 1-.685-1.251c-.1-.484-.15-1.04-.15-1.668Zm11.56-5.408a2.26 2.26 0 0 1-1.19-.32 2.455 2.455 0 0 1-.854-.862 2.27 2.27 0 0 1-.322-1.183c0-.433.107-.827.322-1.182.214-.36.499-.648.854-.862a2.26 2.26 0 0 1 1.19-.321c.423 0 .813.107 1.169.321.36.214.647.502.86.862.215.355.322.75.322 1.182 0 .429-.107.823-.321 1.183-.214.36-.501.647-.861.861a2.223 2.223 0 0 1-1.17.321Z"></path></svg>
                        :
                        <svg role="graphics-symbol" viewBox="0 0 16 16" className="notificationRead" style={{ width: '20.5px', height: '20.5px', display: 'block', fill: 'var(--bs-text-muted', flexShrink: 0, padding: '0px', color: 'var(--bs-text-muted)' }}><path d="M4.44922 13.248C3.81641 13.248 3.33789 13.0859 3.01367 12.7617C2.68945 12.4414 2.52734 11.9688 2.52734 11.3438V4.21289C2.52734 3.58398 2.68945 3.10938 3.01367 2.78906C3.33789 2.46875 3.81641 2.30859 4.44922 2.30859H11.5449C12.1777 2.30859 12.6562 2.46875 12.9805 2.78906C13.3047 3.10938 13.4668 3.58398 13.4668 4.21289V11.3438C13.4668 11.9688 13.3047 12.4414 12.9805 12.7617C12.6562 13.0859 12.1777 13.248 11.5449 13.248H4.44922ZM4.51953 12.0938H11.4746C11.7441 12.0938 11.9512 12.0234 12.0957 11.8828C12.2402 11.7422 12.3125 11.5293 12.3125 11.2441V4.30664C12.3125 4.02539 12.2402 3.81445 12.0957 3.67383C11.9512 3.5332 11.7441 3.46289 11.4746 3.46289H4.51953C4.24609 3.46289 4.03711 3.5332 3.89258 3.67383C3.75195 3.81445 3.68164 4.02539 3.68164 4.30664V11.2441C3.68164 11.5293 3.75195 11.7422 3.89258 11.8828C4.03711 12.0234 4.24609 12.0938 4.51953 12.0938ZM7.37305 10.5527C7.26367 10.5527 7.16602 10.5293 7.08008 10.4824C6.99414 10.4316 6.91016 10.3574 6.82812 10.2598L5.44531 8.57227C5.34375 8.44336 5.29297 8.3125 5.29297 8.17969C5.29297 8.03516 5.3418 7.91211 5.43945 7.81055C5.54102 7.70508 5.66211 7.65234 5.80273 7.65234C5.89258 7.65234 5.97266 7.67188 6.04297 7.71094C6.11719 7.74609 6.18945 7.81055 6.25977 7.9043L7.34961 9.28711L9.66406 5.57812C9.78516 5.37891 9.9375 5.2793 10.1211 5.2793C10.2578 5.2793 10.3809 5.32422 10.4902 5.41406C10.5996 5.50391 10.6543 5.62109 10.6543 5.76562C10.6543 5.83203 10.6387 5.90039 10.6074 5.9707C10.5801 6.03711 10.5469 6.09961 10.5078 6.1582L7.88867 10.2539C7.76367 10.4531 7.5918 10.5527 7.37305 10.5527Z"></path></svg>
                      }
                    </button>

                    {id !== notification.id
                      && (active) && (
                        <button
                          className="border-0 bg-transparent"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setId(notification.id);
                            handleDismiss(notification.id);
                          }}
                          disabled={id}
                          aria-label="button"
                          title="Delete notification"
                        >
                          <i className="fa-solid fa-xmark fs-4 text-hover-danger cursor-pointer" />
                        </button>
                      )}
                    {(id === notification.id) &&
                      <span>
                        <span className="spinner-border spinner-border-sm align-middle ms-2" />
                      </span>
                    }
                  </div>
                  {index !== notifications.length - 1 && <div className="separator separator-dashed my-1 mx-5" />}
                </div>
              ))}
            </InfiniteScroll>
            {notifications?.length === 0 && (<p className="text-center">No notifications to show</p>)}
            {/* </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}
